/**
 * Roles & Permissions — view every role's definition and the permissions it
 * grants, and create / edit / delete CUSTOM roles. Built-in (canonical) roles
 * are read-only here (they're the code-owned security baseline; edit them in
 * canonical.ts + re-seed). Custom roles are fully managed in this UI.
 *
 * Permission changes reach a signed-in user on their NEXT access-token refresh
 * (~15 min) or immediately on re-login — refresh re-derives permissions from
 * the DB (see iam-service.refresh). No forced logout needed.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  ApiError,
  type CreateRoleRequest,
  type IamPermission,
  type IamRole,
  type UpdateRoleRequest,
} from "@dfp/api-client";
import { Badge } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Table, TBody, Td, Th, THead, Tr } from "../components/Table.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

const domainOf = (key: string): string =>
  key.includes(".") ? key.slice(0, key.indexOf(".")) : key;

function groupByDomain(
  perms: readonly IamPermission[],
): ReadonlyArray<readonly [string, readonly IamPermission[]]> {
  const map = new Map<string, IamPermission[]>();
  for (const p of perms) {
    const d = domainOf(p.key);
    const bucket = map.get(d);
    if (bucket) bucket.push(p);
    else map.set(d, [p]);
  }
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

function errText(err: unknown): string {
  return err instanceof ApiError
    ? (err.problem.message ?? err.problem.detail ?? "Request failed")
    : "Network error";
}

export function RolesPage(): JSX.Element {
  const auth = useAuth();
  const qc = useQueryClient();
  const canWrite = (auth.user?.permissions ?? []).includes("iam.role.write");

  const roles = useQuery({
    queryKey: ["iam", "roles"],
    queryFn: () => api.get<readonly IamRole[]>("/api/iam/roles"),
  });
  const catalog = useQuery({
    queryKey: ["iam", "permissions"],
    queryFn: () => api.get<readonly IamPermission[]>("/api/iam/permissions"),
  });

  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const invalidateRoles = (): void => {
    void qc.invalidateQueries({ queryKey: ["iam", "roles"] });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Roles &amp; Permissions</h1>
        <p className="text-sm text-slate-500">
          Every role and what it grants. Built-in roles are read-only; create and edit custom roles
          to fit your team. Changes reach signed-in users within ~15 minutes (their next token
          refresh), or immediately on re-login.
        </p>
      </div>

      {canWrite ? (
        <Card>
          <CardHeader>
            <CardTitle>Custom role</CardTitle>
            {!creating ? (
              <Button
                onClick={() => {
                  setCreating(true);
                  setSelected(null);
                }}
              >
                New role
              </Button>
            ) : null}
          </CardHeader>
          {creating ? (
            <CardBody>
              <RoleForm
                mode="create"
                catalog={catalog.data ?? []}
                onDone={() => {
                  setCreating(false);
                  invalidateRoles();
                }}
                onCancel={() => setCreating(false)}
              />
            </CardBody>
          ) : null}
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Roles</CardTitle>
          <span className="text-xs text-slate-500">{roles.data?.length ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {roles.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : (
            <Table>
              <THead>
                <Tr>
                  <Th>Role</Th>
                  <Th>Definition</Th>
                  <Th>Permissions</Th>
                  <Th>Type</Th>
                  <Th> </Th>
                </Tr>
              </THead>
              <TBody>
                {(roles.data ?? []).map((r) => (
                  <Tr key={r.key}>
                    <Td className="font-mono text-sm font-medium">{r.key}</Td>
                    <Td className="text-sm text-slate-600">{r.description}</Td>
                    <Td className="text-sm">{r.permissionCount}</Td>
                    <Td className="space-x-1">
                      {r.builtIn ? (
                        <Badge tone="neutral">Built-in</Badge>
                      ) : (
                        <Badge tone="success">Custom</Badge>
                      )}
                      {r.requiresApproval ? <Badge tone="warning">Approval</Badge> : null}
                    </Td>
                    <Td>
                      <button
                        type="button"
                        className="text-sm text-brand-600 hover:underline"
                        onClick={() => {
                          setSelected(r.key);
                          setCreating(false);
                        }}
                      >
                        {selected === r.key ? "Hide" : "View"}
                      </button>
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {selected ? (
        <RoleDetail
          roleKey={selected}
          role={(roles.data ?? []).find((r) => r.key === selected)}
          catalog={catalog.data ?? []}
          canWrite={canWrite}
          onClose={() => setSelected(null)}
          onChanged={invalidateRoles}
        />
      ) : null}

      <PermissionCatalog catalog={catalog.data ?? []} />
    </div>
  );
}

function RoleDetail({
  roleKey,
  role,
  catalog,
  canWrite,
  onClose,
  onChanged,
}: {
  readonly roleKey: string;
  readonly role: IamRole | undefined;
  readonly catalog: readonly IamPermission[];
  readonly canWrite: boolean;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const builtIn = role?.builtIn ?? true;

  const perms = useQuery({
    queryKey: ["iam", "roles", roleKey, "permissions"],
    queryFn: () => api.get<readonly string[]>(`/api/iam/roles/${roleKey}/permissions`),
  });

  const del = useMutation({
    mutationFn: () => api.delete<void>(`/api/iam/roles/${roleKey}`),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });

  const granted = perms.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="font-mono">{roleKey}</span>{" "}
          {builtIn ? <Badge tone="neutral">Built-in</Badge> : <Badge tone="success">Custom</Badge>}
        </CardTitle>
        <button type="button" className="text-sm text-slate-500 hover:underline" onClick={onClose}>
          Close
        </button>
      </CardHeader>
      <CardBody className="space-y-4 text-sm">
        {role ? <p className="text-slate-600">{role.description}</p> : null}

        {editing && canWrite && !builtIn ? (
          <RoleForm
            mode="edit"
            roleKey={roleKey}
            catalog={catalog}
            initialDescription={role?.description ?? ""}
            initialPermissions={granted}
            onDone={() => {
              setEditing(false);
              void qc.invalidateQueries({ queryKey: ["iam", "roles", roleKey, "permissions"] });
              onChanged();
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <>
            {perms.isLoading ? (
              <p className="text-slate-500">Loading permissions…</p>
            ) : granted.length === 0 ? (
              <p className="text-slate-500">This role grants no permissions.</p>
            ) : (
              <div className="space-y-3">
                {groupByDomain(catalog.filter((p) => granted.includes(p.key))).map(
                  ([domain, ps]) => (
                    <div key={domain}>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        {domain}
                      </p>
                      <ul className="space-y-1">
                        {ps.map((p) => (
                          <li key={p.key} className="flex gap-2">
                            <span className="font-mono text-xs text-slate-800">{p.key}</span>
                            <span className="text-xs text-slate-500">— {p.description}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ),
                )}
              </div>
            )}

            {builtIn ? (
              <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                Built-in role — read-only. Change its permissions in{" "}
                <span className="font-mono">canonical.ts</span> and re-seed.
              </p>
            ) : canWrite ? (
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  Edit
                </Button>
                <Button
                  variant="danger"
                  disabled={del.isPending}
                  onClick={() => {
                    if (window.confirm(`Delete role "${roleKey}"? This cannot be undone.`)) {
                      del.mutate();
                    }
                  }}
                >
                  {del.isPending ? "Deleting…" : "Delete role"}
                </Button>
                {del.isError ? (
                  <span className="self-center text-xs text-red-700">{errText(del.error)}</span>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}

function RoleForm({
  mode,
  roleKey,
  catalog,
  initialDescription = "",
  initialPermissions = [],
  onDone,
  onCancel,
}: {
  readonly mode: "create" | "edit";
  readonly roleKey?: string;
  readonly catalog: readonly IamPermission[];
  readonly initialDescription?: string;
  readonly initialPermissions?: readonly string[];
  readonly onDone: () => void;
  readonly onCancel: () => void;
}): JSX.Element {
  const [key, setKey] = useState("");
  const [description, setDescription] = useState(initialDescription);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set(initialPermissions));
  const [error, setError] = useState<string | null>(null);

  const toggle = (k: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const save = useMutation({
    mutationFn: () => {
      const permissions = [...selected];
      if (mode === "create") {
        const body: CreateRoleRequest = { key, description, permissions };
        return api.post<{ key: string }>("/api/iam/roles", body);
      }
      const body: UpdateRoleRequest = { description, permissions };
      return api.put<{ key: string }>(`/api/iam/roles/${roleKey}`, body);
    },
    onSuccess: () => {
      setError(null);
      onDone();
    },
    onError: (err: unknown) => setError(errText(err)),
  });

  return (
    <div className="space-y-4">
      {mode === "create" ? (
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">Role key</span>
          <input
            className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm"
            placeholder="e.g. regional_auditor"
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
          <span className="mt-1 block text-[11px] text-slate-400">
            lowercase letters, digits, underscore; starts with a letter. Can't reuse a built-in key.
          </span>
        </label>
      ) : null}

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-slate-600">Description</span>
        <input
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          placeholder="What this role is for"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <div>
        <p className="mb-2 text-xs font-medium text-slate-600">
          Permissions <span className="text-slate-400">({selected.size} selected)</span>
        </p>
        <div className="max-h-96 space-y-3 overflow-y-auto rounded-md border border-slate-200 p-3">
          {groupByDomain(catalog).map(([domain, ps]) => (
            <div key={domain}>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                {domain}
              </p>
              <div className="space-y-1">
                {ps.map((p) => (
                  <label key={p.key} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={selected.has(p.key)}
                      onChange={() => toggle(p.key)}
                    />
                    <span className="font-mono text-xs text-slate-800">{p.key}</span>
                    <span className="text-xs text-slate-500">— {p.description}</span>
                    {p.makerChecker ? <Badge tone="warning">maker-checker</Badge> : null}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {error ? (
        <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </p>
      ) : null}

      <div className="flex gap-2">
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : mode === "create" ? "Create role" : "Save changes"}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function PermissionCatalog({
  catalog,
}: {
  readonly catalog: readonly IamPermission[];
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Permission catalog</CardTitle>
        <span className="text-xs text-slate-500">{catalog.length} — read-only</span>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        <p className="text-xs text-slate-500">
          Permissions are defined in code (each gates an endpoint) and can't be created here — you
          compose them into roles.
        </p>
        {groupByDomain(catalog).map(([domain, ps]) => (
          <div key={domain}>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
              {domain}
            </p>
            <ul className="space-y-1">
              {ps.map((p) => (
                <li key={p.key} className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-slate-800">{p.key}</span>
                  <span className="text-xs text-slate-500">— {p.description}</span>
                  {p.makerChecker ? <Badge tone="warning">maker-checker</Badge> : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
